import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Text, HStack, Circle, VStack } from '@chakra-ui/react';
import { useInterval } from 'usehooks-ts';
import { OnboardingModal } from './components/OnboardingModal';
import '../../containers/prescription/animations.css';
import './animations.css';
import { TimeLimitReached } from './components/TimeLimitReached';
import { ReadyToRecord } from './components/ReadyToRecord';
import { WaitingToRecord } from './components/WaitingToRecord';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { Header } from './components/Header';
import { MobileHeader } from './components/MobileHeader';
import { MinimizedModal } from './components/MinimizedModal';
import { InstructionsCollapse } from './components/InstructionsCollapse';
import { TranscriptionLoading } from './components/TranscriptionLoading';
import { TranscriptionError } from './components/TranscriptionError';
import { TranscriptionSuccess } from './components/TranscriptionSuccess/index';
import { PrescriptionService } from '../../containers/prescription/service';
import { PendingPermission } from './components/PendingPermission';
import { PermissionAlert } from './components/PermissionAlert';
import { PermissionDenied } from './components/PermissionDenied';
import { CancelRecordingDialog } from './components/CancelRecordingDialog';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { getPathname } from '../../helpers/getPathname';
import { get } from 'lodash';
import { isCookieAuthentication, isBasicAuthentication } from '../../helpers/isCookieAuthentication';
import { ShadowOverlay } from './components/ShadowOverlay';
import { useIsMobile } from '../../helpers/useIsMobile';
import { BrowserIncompatibilityModal } from './components/BrowserIncompatibilityModal';
import { trackingService } from '../../shared/eventTracking';
import { amplitudeModule } from '../../shared/amplitude';

interface FloatingAccordionProps {
  prescriptionId: number | undefined;
  onShowIncompleteModal?: () => void;
}

export default function AudioModal({ prescriptionId, onShowIncompleteModal }: FloatingAccordionProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(true);

  const { fetchPrescriptionByReference, prescription } = usePrescriptionStore();
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);

  const prescriptionReference = React.useMemo(() => {
    if (isCookieAuthentication() || isBasicAuthentication()) {
      return get(getPathname().split('/'), [3], null);
    }
    return get(getPathname().split('/'), [2], null);
  }, []);
  const [isRecording, setIsRecording] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false); // Será definido baseado no localStorage
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [microphonePermission, setMicrophonePermission] = useState<'pending' | 'granted' | 'denied'>('pending');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAudioFinished, setIsAudioFinished] = useState(false);
  const [audioData, setAudioData] = useState<number[]>(new Array(20).fill(0));
  const [showTimeLimitReached, setShowTimeLimitReached] = useState(false);
  const [isTimerBlinking, setIsTimerBlinking] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showMicrophoneMenu, setShowMicrophoneMenu] = useState(false);
  const [availableMicrophones, setAvailableMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionSuccess, setTranscriptionSuccess] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showIntroAnimation, setShowIntroAnimation] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasStartedIntro, setHasStartedIntro] = useState(false);
  const [showBrowserIncompatibilityModal, setShowBrowserIncompatibilityModal] = useState(false);

  // Tempo máximo de gravação em segundos
  const MAX_RECORDING_TIME = 60;
  const TIME_TO_SHOW_BLINKING_RECORDING_TIME = 10;

  const AUDIO_MIMETYPE = 'audio/webm';
  const AUDIO_FILE_EXTENSION = 'webm';

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const isOpen = internalIsOpen;

  const isMobile = useIsMobile() || false;

  // Animação de entrada - sempre inicia quando componente monta
  useEffect(() => {
    if (!hasStartedIntro) {
      setHasStartedIntro(true);
      setInternalIsOpen(false);
      setShowIntroAnimation(true);

      setIsExpanded(true);
    }
  }, [hasStartedIntro]);

  const ONBOARDING_STORAGE_KEY = 'microphone-onboarding-completed';

  const hasCompletedOnboarding = useCallback(() => {
    try {
      const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      return completed === 'true';
    } catch (error) {
      return false;
    }
  }, []);

  const markOnboardingAsCompleted = useCallback(() => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    } catch (error) {
      // Silently fail
    }
  }, []);

  const onboardingSteps = [
    {
      title: 'Transcreva com IA',
      description: 'Agora ficou mais fácil prescrever medicamentos e exames',
      buttonText: 'Próximo',
    },
    {
      title: 'Prescreva medicamentos',
      description: 'Informe a posologia, dosagem, frequência e contraindicação',
      buttonText: 'Próximo',
    },
    {
      title: 'Prescreva exames',
      description: 'Informe o nome completo do exame e a indicação clínica do pedido',
      buttonText: 'Começar',
    },
  ];

  const handleToggle = () => {
    trackingService.trackPrescriptionVoiceOpenWindow();
    amplitudeModule.voicePrescriptionClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
    });
    if (!hasCompletedOnboarding()) {
      setShowOnboarding(true);
    } else {
      setInternalIsOpen(true);
    }
  };

  const handleCloseIntro = () => {
    trackingService.trackPrescriptionVoiceReduceWindow();
    setShowIntroAnimation(false);
    setIsExpanded(false);
  };

  const handleExpandedClick = () => {
    trackingService.trackPrescriptionVoiceOpenWindow();
    amplitudeModule.voicePrescriptionClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
    });
    setShowIntroAnimation(false);
    setIsExpanded(false);

    if (!hasCompletedOnboarding()) {
      setShowOnboarding(true);
    } else {
      setInternalIsOpen(true);
    }
  };

  const handleClose = () => {
    trackingService.trackPrescriptionVoiceCloseWindow();
    setInternalIsOpen(false);
  };

  const handleStartRecording = async () => {
    if (microphonePermission === 'pending') {
      await requestMicrophonePermission();
    } else if (microphonePermission === 'granted') {
      trackingService.trackPrescriptionVoiceBegin('allowed');
      await startRecording();
    }
  };

  const handleOnboardingNext = () => {
    if (currentOnboardingStep < onboardingSteps.length - 1) {
      const stepMap = ['step1', 'step2'] as const;
      trackingService.trackPrescriptionVoiceOnboardingNext(stepMap[currentOnboardingStep] as 'step1' | 'step2');
      setCurrentOnboardingStep(currentOnboardingStep + 1);
    } else {
      trackingService.trackPrescriptionVoiceOnboardingNext('final');
      markOnboardingAsCompleted();
      handleStartRecording();
      setShowOnboarding(false);
      setInternalIsOpen(true);
    }
  };

  const handleOnboardingClose = () => {
    markOnboardingAsCompleted();
    setShowOnboarding(false);
  };

  const checkMediaRecorderCompatibility = () => {
    return (
      typeof MediaRecorder !== 'undefined' &&
      MediaRecorder.isTypeSupported &&
      MediaRecorder.isTypeSupported('audio/webm')
    );
  };

  const requestMicrophonePermission = async () => {
    try {
      if (microphonePermission === 'granted') {
        markOnboardingAsCompleted();
        setShowOnboarding(false);
        setInternalIsOpen(true);
        trackingService.trackPrescriptionVoiceBegin('allowed');
        startRecording();
        return;
      }

      if (microphonePermission === 'pending') {
        trackingService.trackPrescriptionVoiceEnableMicrophone();
      }

      if (microphonePermission === 'denied') {
        trackingService.trackPrescriptionVoicePermissionDeniedRetry();
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermission('granted');
      setShowOnboarding(false);
      streamRef.current = stream;

      trackingService.trackPrescriptionVoiceBegin('allowed');

      // Configurar análise de áudio para espectro
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      await getAvailableMicrophones();
    } catch (error) {
      setMicrophonePermission('denied');
      trackingService.trackPrescriptionVoiceBegin('denied');
    }
  };

  // Função para atualizar dados de áudio para espectro
  const updateAudioData = () => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const update = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);

      // Processar apenas as primeiras 20 frequências para o espectro minimalista
      const processedData = Array.from(dataArray.slice(0, 20));

      setAudioData(processedData);

      // Continuar animação se o microfone estiver ativo
      if (microphonePermission === 'granted') {
        animationFrameRef.current = requestAnimationFrame(update);
      }
    };

    update();
  };

  const startRecording = async () => {
    if (!checkMediaRecorderCompatibility()) {
      setShowBrowserIncompatibilityModal(true);
      return;
    }

    amplitudeModule.voicePrescriptionRecordClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
    });

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setMicrophonePermission('granted');
    setShowOnboarding(false);
    streamRef.current = stream;

    setRecordingTime(MAX_RECORDING_TIME);

    // Configurar análise de áudio para espectro
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    if (!streamRef.current) return;
    const audioTracks = streamRef.current.getAudioTracks();
    audioTracks.forEach((track) => {
      track.enabled = true;
    });

    setIsAudioFinished(false);
    setShowTimeLimitReached(false);

    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: AUDIO_MIMETYPE,
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);

      updateAudioData();
    } catch (error) {
      // Silently fail
    }
  };

  useInterval(
    () => {
      setRecordingTime((prev) => Math.max(0, prev - 0.1));
    },
    isRecording ? 100 : null
  );

  const reloadPrescriptionData = useCallback(async () => {
    try {
      if (prescriptionReference) {
        const updatedPrescription = await fetchPrescriptionByReference(prescriptionReference);

        if (updatedPrescription?.Itens) {
          const medicinesWithoutPosology = updatedPrescription.Itens.filter((medicine) => {
            const posology = medicine.Posologia;
            return (
              posology === 'None' ||
              posology === null ||
              posology === 'null' ||
              posology === undefined ||
              posology?.trim() === ''
            );
          });

          if (medicinesWithoutPosology.length > 0 && onShowIncompleteModal) {
            onShowIncompleteModal();
          }
        }
      }
    } catch (error) {
      // Silently fail
    }
  }, [
    prescriptionReference,
    fetchPrescriptionByReference,
    onShowIncompleteModal,
  ]);

  const sendAudioToTranscriptionApi = useCallback(async () => {
    try {
      setIsTranscribing(true);
      setTranscriptionSuccess(false);
      setTranscriptionError(false);
      setShowTimeLimitReached(false);

      // Verificar se há chunks de áudio
      if (audioChunksRef.current.length === 0) {
        setIsTranscribing(false);
        return;
      }

      // Criar blob do áudio gravado
      const audioBlob = new Blob(audioChunksRef.current, {
        type: mediaRecorderRef.current?.mimeType || AUDIO_MIMETYPE,
      });

      // Verificar se o blob tem conteúdo
      if (audioBlob.size === 0) {
        return;
      }

      const formData = new FormData();
      formData.append('audio_file', audioBlob, `audio.${AUDIO_FILE_EXTENSION}`);
      formData.append('prescriptionId', prescriptionId ? prescriptionId.toString() : '');

      await PrescriptionService.createPrescriptionByAudio(formData);

      trackingService.trackPrescriptionVoiceTransactionSuccess();

      setIsTranscribing(false);
      setTranscriptionSuccess(true);

      await reloadPrescriptionData();

      const updatedPrescription = usePrescriptionStore.getState().prescription;
      const hasPosology = !(updatedPrescription?.Itens ?? []).some((medicine) => {
        const posology = medicine.Posologia;
        return !posology || posology === 'None' || posology === 'null' || posology.trim() === '';
      });

      amplitudeModule.voicePrescriptionConfirmed({
        validation_code: updatedPrescription?.CodigoValidacao ?? '',
        has_posology: hasPosology,
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
      });

      setTimeout(() => {
        setTranscriptionSuccess(false);
        setInternalIsOpen(false);
      }, 2000);
    } catch (error) {
      setIsTranscribing(false);
      setTranscriptionSuccess(false);
      setTranscriptionError(true);
    }
  }, [prescriptionId, reloadPrescriptionData, prescriberConfiguration]);

  // Função para recomeçar o fluxo de gravação
  const restartRecording = useCallback(() => {
    trackingService.trackPrescriptionVoiceRetryUpload();

    // Parar gravação se estiver ativa
    if (isRecording && mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        // Silently fail
      }
    }

    // Cancelar animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Parar e limpar streams ativos
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }

    // Limpar AudioContext
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      } catch (error) {}
      audioContextRef.current = null;
    }

    // Limpar AnalyserNode
    if (analyserRef.current) {
      analyserRef.current = null;
    }

    // Limpar MediaRecorder
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }

    // Limpar chunks de áudio
    audioChunksRef.current = [];

    // Limpar todos os estados
    setTranscriptionError(false);
    setTranscriptionSuccess(false);
    setIsTranscribing(false);
    setIsAudioFinished(false);
    setIsRecording(false);
    setRecordingTime(MAX_RECORDING_TIME);
    setAudioData(new Array(20).fill(0));
    setIsMuted(false);
    setIsMinimized(false);
    setShowTimeLimitReached(false);
    setIsTimerBlinking(false);
    setIsPaused(false);
  }, [MAX_RECORDING_TIME, isRecording]);

  const stopRecording = useCallback(
    (isTimeLimit = false) => {
      if (mediaRecorderRef.current && !isAudioFinished) {
        trackingService.trackPrescriptionVoiceStop(isTimeLimit ? 'timeout' : 'manual');

        // Parar análise de áudio
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        // Configurar evento onstop para processar áudio após parar
        mediaRecorderRef.current.onstop = () => {
          if (isTimeLimit) {
            setTimeout(() => {
              sendAudioToTranscriptionApi();
            }, 2000);
          } else {
            sendAudioToTranscriptionApi();
          }
        };

        mediaRecorderRef.current.stop();

        if (isTimeLimit) {
          setShowTimeLimitReached(true);
          setRecordingTime(0);
        } else {
          setRecordingTime(MAX_RECORDING_TIME);
        }

        setIsAudioFinished(true);
        setIsRecording(false);
        setIsPaused(false);
        setAudioData([]);
      }
    },
    [isAudioFinished, sendAudioToTranscriptionApi]
  );

  const toggleRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  useEffect(() => {
    if (recordingTime <= 0 && isRecording) {
      stopRecording(true);
    }
  }, [recordingTime, isRecording, stopRecording]);

  const cancelRecording = () => {
    trackingService.trackPrescriptionVoiceCancel('userCancel');
    if (isRecording) {
      setShowCancelDialog(true);
    } else {
      resetToInitialState();
    }
  };

  const resetToInitialState = () => {
    // Parar gravação se estiver ativa
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // Parar stream se estiver ativo
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Parar análise de áudio
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Limpar chunks de áudio
    audioChunksRef.current = [];

    // Resetar todos os estados para o estado inicial
    setIsRecording(false);
    setIsPaused(false);
    setIsAudioFinished(false);
    setRecordingTime(MAX_RECORDING_TIME);
    setAudioData(new Array(20).fill(0));
    setIsTimerBlinking(false);
    setIsMuted(false);
    setIsTranscribing(false);
    setTranscriptionSuccess(false);
    setTranscriptionError(false);
    setShowCancelDialog(false);

    // Manter modal aberto para mostrar ReadyToRecord
    setInternalIsOpen(true);
  };

  const handleCancelConfirm = () => {
    trackingService.trackPrescriptionVoiceCancelConfirm();
    amplitudeModule.voicePrescriptionCancelled({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
    });
    resetToInitialState();
  };

  const handleCancelCancel = () => {
    trackingService.trackPrescriptionVoiceCancelDeny();
    setShowCancelDialog(false);
    // Continuar gravação normalmente
  };

  const handleBrowserIncompatibilityClose = () => {
    setShowBrowserIncompatibilityModal(false);
    setInternalIsOpen(false);
    setShowIntroAnimation(false);
    setIsExpanded(false);
  };

  // Função para fechar tela de tempo limite
  const closeTimeLimitScreen = () => {
    setShowTimeLimitReached(false);
    setRecordingTime(MAX_RECORDING_TIME);
  };

  // Função para formatar tempo
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Função para formatar tempo máximo em minutos ou segundos
  const formatMaxTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins < 1) {
      return `${seconds} seg`;
    }

    // Formato mm:ss para tempos maiores que 1 minuto
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Função para calcular tempo já gravado
  const getElapsedTime = () => {
    return Math.round((MAX_RECORDING_TIME - recordingTime) * 10) / 10;
  };

  // Função para controlar mute do microfone
  const toggleMute = useCallback(() => {
    if (streamRef.current) {
      const audioTracks = streamRef.current.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = isMuted;
      });
      trackingService.trackPrescriptionVoiceMute(isMuted ? 'unmute' : 'mute');
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const getAvailableMicrophones = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter((device) => device.kind === 'audioinput');
      setAvailableMicrophones(microphones);
      if (microphones.length > 0 && !selectedMicrophone) {
        setSelectedMicrophone(microphones[0].deviceId);
      }
    } catch (error) {}
  }, [selectedMicrophone]);

  const switchMicrophone = async (deviceId: string) => {
    trackingService.trackPrescriptionVoiceSwitchMicrophone();
    setSelectedMicrophone(deviceId);
    setShowMicrophoneMenu(false);

    if (isRecording && streamRef.current && mediaRecorderRef.current) {
      try {
        // Obter novo stream com o microfone selecionado
        const newStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        });

        // Salvar o estado atual do MediaRecorder
        const currentState = mediaRecorderRef.current.state;
        const currentChunks = [...audioChunksRef.current];

        // Parar o MediaRecorder atual
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }

        // Parar o stream antigo
        streamRef.current.getTracks().forEach((track) => track.stop());

        // Atualizar referências
        streamRef.current = newStream;

        // Criar novo MediaRecorder com o novo stream
        const newMediaRecorder = new MediaRecorder(newStream, { mimeType: mediaRecorderRef.current.mimeType });
        mediaRecorderRef.current = newMediaRecorder;

        // Restaurar os chunks anteriores
        audioChunksRef.current = currentChunks;

        // Configurar event listeners do novo MediaRecorder
        newMediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        newMediaRecorder.onstop = () => {
          // Manter o comportamento original de parada
        };

        // Reiniciar gravação se estava gravando
        if (currentState === 'recording') {
          newMediaRecorder.start(1000); // 1 segundo de intervalo
        }

        // Atualizar AudioContext para o spectrum com o novo stream
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        audioContextRef.current = new AudioContext();
        const source = audioContextRef.current.createMediaStreamSource(newStream);
        const analyser = audioContextRef.current.createAnalyser();
        source.connect(analyser);
        analyserRef.current = analyser;

        // Reiniciar análise de áudio
        updateAudioData();
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (recordingTime <= TIME_TO_SHOW_BLINKING_RECORDING_TIME && recordingTime > 0 && isRecording) {
      setIsTimerBlinking(true);
    } else {
      setIsTimerBlinking(false);
    }
  }, [recordingTime, isRecording]);

  useEffect(() => {
    if (!isRecording) {
      setIsMinimized(false);
      setIsMuted(false);
    }
  }, [isRecording]);

  // Listener para detectar mudanças na permissão do microfone
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkPermissionStatus = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

        if (permissionStatus.state !== microphonePermission) {
          if (permissionStatus.state === 'granted') {
            setMicrophonePermission('granted');
            await getAvailableMicrophones();
          } else if (permissionStatus.state === 'denied') {
            setMicrophonePermission('denied');
          } else {
            setMicrophonePermission('pending');
          }
        }
      } catch (error) {
        // Fallback: tentar acessar o microfone para verificar permissão
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((track) => track.stop());
          if (microphonePermission !== 'granted') {
            setMicrophonePermission('granted');
            await getAvailableMicrophones();
          }
        } catch (permissionError) {
          if (microphonePermission !== 'denied') {
            setMicrophonePermission('denied');
          }
        }
      }
    };

    // Verificar imediatamente
    checkPermissionStatus();

    // Verificar periodicamente para detectar mudanças
    intervalId = setInterval(checkPermissionStatus, 2000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [microphonePermission, getAvailableMicrophones]);

  useEffect(() => {
    getAvailableMicrophones();
  }, [getAvailableMicrophones]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMicrophoneMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-microphone-menu]')) {
          setShowMicrophoneMenu(false);
        }
      }
    };

    if (showMicrophoneMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMicrophoneMenu]);

  const checkExistingMicrophonePermission = useCallback(async () => {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

      if (permissionStatus.state === 'granted') {
        setMicrophonePermission('granted');
        return true;
      }
    } catch (error) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setMicrophonePermission('granted');
        stream.getTracks().forEach((track) => track.stop());
        return true;
      } catch (permissionError) {
        setMicrophonePermission('denied');
        return false;
      }
    }

    setMicrophonePermission('pending');
    return false;
  }, []);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setIsLoading(true);

        await checkExistingMicrophonePermission();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, [checkExistingMicrophonePermission]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isRecording) {
        if (event.code === 'Space') {
          event.preventDefault();
          toggleMute();
        } else if (event.code === 'Enter') {
          event.preventDefault();
          toggleRecording();
        }
      }
    };

    if (isRecording) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isRecording, isMuted, toggleMute, toggleRecording]);

  // Cleanup ao desmontar componente
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Renderizar OnboardingModal
  if (showOnboarding) {
    return (
      <>
        {isMobile && <ShadowOverlay />}
        <OnboardingModal
          isVisible={showOnboarding}
          currentStep={currentOnboardingStep}
          steps={onboardingSteps}
          onNext={handleOnboardingNext}
          onSkip={handleOnboardingClose}
          isMobile={isMobile}
        />
      </>
    );
  }

  if (!isOpen) {
    if (showIntroAnimation && isExpanded) {
      return (
        <HStack
          position="fixed"
          bottom={isMobile ? '15%' : '10%'}
          right={isMobile ? '20px' : '125px'}
          zIndex={11}
          spacing="8px"
          alignItems="center"
        >
          <Box
            bg="white"
            borderRadius="30px"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.15)"
            p="12px 16px"
            display="flex"
            alignItems="center"
            gap="12px"
            cursor="pointer"
            onClick={handleExpandedClick}
            height="60px"
            minW="280px"
            transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
            transform="scale(1)"
            opacity="1"
            animation="slideIn 0.4s ease-out"
          >
            <Circle size="36px" bg="#FFB3D1" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <HStack spacing="2px">
                <Box width="2px" height="6px" bg="#6B46C1" borderRadius="1px" />
                <Box width="2px" height="10px" bg="#6B46C1" borderRadius="1px" />
                <Box width="2px" height="14px" bg="#6B46C1" borderRadius="1px" />
                <Box width="2px" height="10px" bg="#6B46C1" borderRadius="1px" />
                <Box width="2px" height="6px" bg="#6B46C1" borderRadius="1px" />
              </HStack>
            </Circle>

            <VStack spacing="2px" align="flex-start" flex="1">
              <Text fontSize="14px" fontWeight="600" color="#3D0E6B" lineHeight="16px">
                Transcreva documentos por áudio
              </Text>
              <Text fontSize="12px" color="#6B7280" lineHeight="14px">
                Powered by Nuvie
              </Text>
            </VStack>
          </Box>

          <Box
            cursor="pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseIntro();
            }}
            p="6px"
            borderRadius="6px"
            _hover={{ bg: 'gray.50' }}
            transition="all 0.2s ease"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box width="20px" height="20px" position="relative">
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%) rotate(45deg)"
                width="16px"
                height="2px"
                bg="#6B7280"
                borderRadius="1px"
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%) rotate(-45deg)"
                width="16px"
                height="2px"
                bg="#6B7280"
                borderRadius="1px"
              />
            </Box>
          </Box>
        </HStack>
      );
    }

    // Estado normal - círculo
    return (
      <Box
        position="fixed"
        bottom={isMobile ? '15%' : '10%'}
        right={isMobile ? '20px' : '125px'}
        zIndex={11}
        cursor="pointer"
        onClick={handleToggle}
      >
        <Circle
          size="60px"
          bg="pink.200"
          boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
          _hover={{ bg: 'pink.300' }}
          transition="all 0.3s ease"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <HStack spacing="3px">
            <Box width="3px" height="8px" bg="purple.600" borderRadius="1px" />
            <Box width="3px" height="12px" bg="purple.600" borderRadius="1px" />
            <Box width="3px" height="16px" bg="purple.600" borderRadius="1px" />
            <Box width="3px" height="12px" bg="purple.600" borderRadius="1px" />
            <Box width="3px" height="8px" bg="purple.600" borderRadius="1px" />
          </HStack>
        </Circle>
      </Box>
    );
  }

  // Componente minimizado
  if (isMinimized && isRecording) {
    return (
      <>
        <MinimizedModal
          audioData={audioData}
          recordingTime={recordingTime}
          toggleRecording={toggleRecording}
          isMuted={isMuted}
          toggleMute={toggleMute}
          setIsMinimized={setIsMinimized}
          MAX_RECORDING_TIME={MAX_RECORDING_TIME}
          onClose={cancelRecording}
          getElapsedTime={getElapsedTime}
          isMobile={isMobile}
        />

        {/* Modal de confirmação de cancelamento para modo minimizado */}
        <CancelRecordingDialog
          isOpen={showCancelDialog}
          onClose={() => setShowCancelDialog(false)}
          onConfirm={handleCancelConfirm}
          onCancel={handleCancelCancel}
        />
      </>
    );
  }

  if (isLoading && !showIntroAnimation) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      {(isTranscribing || (isMobile && isOpen)) && <ShadowOverlay />}

      <Box
        position="fixed"
        bottom={isMobile ? '0' : '10%'}
        right={isMobile ? '0' : '125px'}
        left={isMobile ? '0' : 'auto'}
        top={isMobile ? '0' : 'auto'}
        zIndex={12}
        bg="white"
        borderRadius={isMobile ? '0' : '16px'}
        boxShadow={isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.12)'}
        border={isMobile ? 'none' : '1px solid'}
        borderColor="gray.100"
        minWidth={isMobile ? '100%' : '320px'}
        maxWidth={isMobile ? '100%' : '320px'}
        maxHeight={isMobile ? '100vh' : '500px'}
        height={isMobile ? '100vh' : '100%'}
        width={isMobile ? '100vw' : 'auto'}
        transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
        transform={isMinimized ? 'scale(0.9) translateY(5px)' : 'scale(1) translateY(0px)'}
        opacity={isMinimized ? 0.8 : 1}
        display="flex"
        flexDirection="column"
      >
        {isMobile ? (
          <MobileHeader
            isRecording={isRecording}
            setIsMinimized={setIsMinimized}
            setInternalIsOpen={setInternalIsOpen}
            handleClose={isRecording ? cancelRecording : handleClose}
          />
        ) : (
          <Header
            isRecording={isRecording}
            setIsMinimized={setIsMinimized}
            setInternalIsOpen={setInternalIsOpen}
            showTimeLimitReached={showTimeLimitReached}
            closeTimeLimitScreen={closeTimeLimitScreen}
            handleClose={isRecording ? cancelRecording : handleClose}
          />
        )}

        <Box flex="1" overflowY="auto" display="flex" flexDirection="column">
          {microphonePermission === 'granted' && !isRecording && !showTimeLimitReached && (
            <InstructionsCollapse showInstructions={showInstructions} setShowInstructions={setShowInstructions} />
          )}

          {/* Alerta de permissão - fixo no topo, fora do box centralizado */}
          {microphonePermission === 'pending' && <PermissionAlert isMobile={isMobile} />}

          {/* Wrapper para centralizar conteúdo apenas no mobile */}
          <Box flex="1" display="flex" flexDirection="column" justifyContent={isMobile ? 'center' : 'flex-start'}>
            {/* Loading de transcrição */}
            {isTranscribing && <TranscriptionLoading isMobile={isMobile} />}

            {/* Sucesso da transcrição */}
            {transcriptionSuccess && <TranscriptionSuccess />}

            {/* Erro na transcrição */}
            {transcriptionError && <TranscriptionError onRestart={restartRecording} />}

            {/* Estado de permissão pendente */}
            {microphonePermission === 'pending' && (
              <>
                <PendingPermission
                  recordingTime={recordingTime}
                  formatTime={formatTime}
                  formatMaxTime={formatMaxTime}
                  MAX_RECORDING_TIME={MAX_RECORDING_TIME}
                  handleStartRecording={handleStartRecording}
                  isMobile={isMobile}
                />
              </>
            )}

            {/* Estado de permissão negada */}
            {microphonePermission === 'denied' && <PermissionDenied onRetry={requestMicrophonePermission} />}

            {/* Estado de tempo limite atingido */}
            {showTimeLimitReached && <TimeLimitReached />}

            {/* Estado de gravação ativa */}
            {microphonePermission === 'granted' && isRecording && !showTimeLimitReached && (
              <ReadyToRecord
                isRecording={isRecording}
                audioData={audioData}
                recordingTime={recordingTime}
                isTimerBlinking={isTimerBlinking}
                isPaused={isPaused}
                toggleRecording={toggleRecording}
                cancelRecording={cancelRecording}
                isMuted={isMuted}
                toggleMute={toggleMute}
                availableMicrophones={availableMicrophones}
                selectedMicrophone={selectedMicrophone}
                switchMicrophone={switchMicrophone}
                showMicrophoneMenu={showMicrophoneMenu}
                setShowMicrophoneMenu={setShowMicrophoneMenu}
                formatTime={formatTime}
                isMobile={isMobile}
              />
            )}

            {/* Estado inicial (após permissão concedida, antes de gravar) */}
            {microphonePermission === 'granted' && !isRecording && !showTimeLimitReached && (
              <WaitingToRecord
                formatTime={formatTime}
                formatMaxTime={formatMaxTime}
                MAX_RECORDING_TIME={MAX_RECORDING_TIME}
                handleStartRecording={handleStartRecording}
                isMobile={isMobile}
              />
            )}

            {/* Spacer para empurrar o footer para o fundo - apenas na versão desktop */}
            {!isMobile && <Box flex="1" />}
          </Box>

          {/* Footer - fixo na parte inferior */}
          <Box pb="16px" pt="16px">
            <Text fontSize="12px" color="gray.400" textAlign="center">
              Powered by Nuvie
            </Text>
          </Box>
        </Box>

        {/* Modal de confirmação de cancelamento */}
        <CancelRecordingDialog
          isOpen={showCancelDialog}
          onClose={() => setShowCancelDialog(false)}
          onConfirm={handleCancelConfirm}
          onCancel={handleCancelCancel}
        />

        <BrowserIncompatibilityModal
          isOpen={showBrowserIncompatibilityModal}
          onClose={handleBrowserIncompatibilityClose}
        />
      </Box>
    </>
  );
}
